import { pb, escapeFilterValue } from '../lib/pocketbase'
import type { Invoice, InvoiceStatus, InvoiceLineItem } from '../types'
import { ensureUserAgency } from '../utils/ensureAgency'
import { parseDateOnly, startOfLocalDay, localDateString } from '../utils/formatters'

export type InvoicePayload = {
  invoice_number: string // Required
  status: InvoiceStatus // Required
  subtotal: number // Required
  total: number // Required
  issue_date: string // Required
  due_date: string // Required
  title?: string | null
  tax_rate?: number | null
  tax_amount?: number | null
  amount_paid?: number | null
  balance_due?: number | null
  paid_date?: string | null
  line_items?: InvoiceLineItem[] | null
  client_id?: string | null
  deal_id?: string | null
  organization_id?: string | null
  bill_to_name?: string | null
  bill_to_address?: string | null
  bill_to_email?: string | null
  payment_terms?: string | null
  payment_instructions?: string | null
  notes?: string | null
  agency_id?: string
  created_by?: string | null
}

export type InvoiceFilters = {
  status?: InvoiceStatus
  client_id?: string
  deal_id?: string
  organization_id?: string
  start_date?: string
  end_date?: string
  // Legacy filter aliases
  contract_id?: string // Alias for deal_id
  related_to?: string
  related_id?: string
}

function mapInvoice(record: Record<string, any>): Invoice {
  const total = record.total ?? 0
  return {
    id: record.id,
    invoice_number: record.invoice_number ?? '',
    title: record.title ?? null,
    status: (record.status ?? 'draft') as InvoiceStatus,
    subtotal: record.subtotal ?? 0,
    tax_rate: record.tax_rate ?? null,
    tax_amount: record.tax_amount ?? null,
    total: total,
    amount_paid: record.amount_paid ?? null,
    balance_due: record.balance_due ?? null,
    issue_date: record.issue_date ?? '',
    due_date: record.due_date ?? '',
    paid_date: record.paid_date ?? null,
    line_items: Array.isArray(record.line_items) ? record.line_items : null,
    client_id: record.client_id ?? null,
    deal_id: record.deal_id ?? null,
    organization_id: record.organization_id ?? null,
    bill_to_name: record.bill_to_name ?? null,
    bill_to_address: record.bill_to_address ?? null,
    bill_to_email: record.bill_to_email ?? null,
    payment_terms: record.payment_terms ?? null,
    payment_instructions: record.payment_instructions ?? null,
    notes: record.notes ?? null,
    agency_id: record.agency_id ?? '',
    created_by: record.created_by ?? null,
    created: record.created ?? '',
    updated: record.updated ?? '',
    // Legacy aliases for backward compatibility
    total_amount: total,
    owner_id: record.created_by ?? '',
    contract_id: record.deal_id ?? null,
    related_to: record.client_id ? 'client' : null,
    related_id: record.client_id ?? null,
  }
}

export async function listInvoices(filters?: InvoiceFilters) {
  
  // Get current user's agency for filtering
  const user = pb.authStore.model
  const agencyId = (user as any)?.agency_id

  // If no agency, return empty - user needs to complete onboarding
  if (!agencyId) {
    return []
  }

  const filterParts: string[] = [`agency_id = "${escapeFilterValue(agencyId)}"`]

  if (filters?.status) {
    filterParts.push(`status = "${escapeFilterValue(filters.status)}"`)
  }
  // Support client_id filter or legacy related_to/related_id pattern
  const clientId = filters?.client_id || (filters?.related_to === 'client' ? filters?.related_id : undefined)
  if (clientId) {
    filterParts.push(`client_id = "${escapeFilterValue(clientId)}"`)
  }
  // Support deal_id or legacy contract_id
  const dealId = filters?.deal_id || filters?.contract_id
  if (dealId) {
    filterParts.push(`deal_id = "${escapeFilterValue(dealId)}"`)
  }
  if (filters?.organization_id) {
    filterParts.push(`organization_id = "${escapeFilterValue(filters.organization_id)}"`)
  }
  if (filters?.start_date) {
    filterParts.push(`due_date >= "${escapeFilterValue(filters.start_date)}"`)
  }
  if (filters?.end_date) {
    filterParts.push(`due_date <= "${escapeFilterValue(filters.end_date)}"`)
  }

  const filter = filterParts.join(' && ')

  const options: any = {
    sort: '-due_date',
    filter,
    expand: 'client_id,deal_id,organization_id,created_by',
  }

  const data = await pb.collection('invoices').getFullList<Record<string, any>>(options)
  return data.map(mapInvoice)
}

export async function getInvoice(invoiceId: string) {
  
  const record = await pb.collection('invoices').getOne<Record<string, any>>(invoiceId, {
    expand: 'client_id,deal_id,organization_id,created_by',
  })
  return mapInvoice(record)
}

export async function createInvoice(payload: InvoicePayload, userId: string) {
  
  const agencyId = await ensureUserAgency()
  const createPayload: any = {
    ...payload,
    agency_id: agencyId,
    created_by: userId,
  }
  // Remove undefined/null values
  Object.keys(createPayload).forEach(key => {
    if (createPayload[key] === undefined) {
      delete createPayload[key]
    }
  })
  const record = await pb.collection('invoices').create(createPayload)
  return mapInvoice(record)
}

export async function updateInvoice(invoiceId: string, payload: Partial<InvoicePayload>) {
  
  const record = await pb.collection('invoices').update(invoiceId, payload)
  return mapInvoice(record)
}

export async function deleteInvoice(invoiceId: string) {
  
  await pb.collection('invoices').delete(invoiceId)
}

export async function markInvoicePaid(invoiceId: string) {
  return updateInvoice(invoiceId, { status: 'paid' })
}

export async function listClientInvoices(clientId: string) {
  return listInvoices({ client_id: clientId })
}

export async function listDealInvoices(dealId: string) {
  return listInvoices({ deal_id: dealId })
}

// Legacy alias for backward compatibility
export async function listContractInvoices(contractId: string) {
  return listInvoices({ deal_id: contractId })
}

export async function listOverdueInvoices() {
  
  // Get current user's agency for filtering (security: agency isolation)
  const user = pb.authStore.model
  const agencyId = (user as any)?.agency_id

  if (!agencyId) {
    return []
  }

  const today = localDateString()
  const data = await pb.collection('invoices').getFullList<Record<string, any>>({
    filter: `agency_id = "${escapeFilterValue(agencyId)}" && due_date < "${escapeFilterValue(today)}" && status != "paid"`,
    sort: '-due_date',
  })
  return data.map(mapInvoice)
}

// Helper functions
export function isInvoiceOverdue(invoice: Invoice): boolean {
  if (invoice.status === 'paid' || invoice.status === 'cancelled') return false
  const dueDate = parseDateOnly(invoice.due_date)
  if (!dueDate) return false
  return startOfLocalDay(dueDate) < startOfLocalDay(new Date())
}

